import { t } from 'i18next';
import { forwardRef } from 'react';

import useE164 from '../../utils/E164';

import LabeledInput from './LabeledInput';

/**Specialized LabeledInput component
 * @param {object} props Incoming JSX attributes.
 * @param {boolean} [props.autofocus] A boolean value that controls whether or not the input is focused automatically when navigating to the view.
 * @param {string} [props.className] A string of HTML classes delimited by a space character to be concatenated with the default component class for specific component targeting in styles or functionality.
 * @param {boolean} [props.disabled] A boolean value that will override the doingAjax UserState for variable control over when the component is enabled.
 * @param {boolean} [props.forceValidation] A boolean value that controls whether or not the input is validated onChange.
 * @param {string} [props.formKey] A string value that should be a path to the form state value represented by this component delimited by a "." character.
 * @param {string} [props.initialValue] A string value that will be directly passed to the HTML value attribute of the created element on first render.
 * @param {string} [props.labelText] A string value to be used as the label text. Default value is blank.
 * @param {function} [props.onChange] A function to be executed during the created elements onChange event. Receives the new value from the event object.
 * @param {string} [props.placeholder] A string value that will be displayed within the input when the input is empty.
 * @param {object} [ref] A React Reference created via useReference or a mapper function.
 * @param {string} [props.type] A string value that corresponds to the desired type of the input.
 * @param {object} [props.validatedObject] An object that must be created via the useValidatedInput Hook.
 * @param {object} [props.validators] An array of objects. Each object in the array is evaluated during the validation effect allowing for multiple constraints on a value.
 * @returns {JSX.Element} Specialized LabeledInput component
 * @see {@link https://bitbucket.org/pcgeducation/shipyard/src/master/shipyard-web/src/components/shared/LabeledInput.md LabeledInput Documentation} for further information
 * @see {@link https://bitbucket.org/pcgeducation/shipyard/src/master/shipyard-web/src/components/shared/Phone.md Phone Documentation} for further information
 */

const Phone = forwardRef(({ labelText, validators, placeHolder, ...restOfProps }, ref) => {
    const { normalize } = useE164();
    let phoneValidators = [
        {
            rgx: /^(?:(?:\+)?1|)?[(\-. ]*(\d{3})[)\-. ]*(\d{3})[-. ]*(\d{4})$/,
            failText: t('Phone.EnterPhoneValid'),
        },
    ];

    if (validators) {
        let defaultValidators = phoneValidators.values();
        for (let v1 of defaultValidators) {
            let newValidators = validators.values();
            for (let v2 of newValidators) {
                if (v1.rgx.toString() === v2.rgx.toString()) {
                    v1.failText = v2.failText;
                }
            }
        }
        phoneValidators.concat(validators);
        phoneValidators = [...new Set(phoneValidators)];
    }

    return (
        <LabeledInput
            validators={phoneValidators}
            type="phone"
            labelText={labelText ? labelText : t('Phone.PhoneNumberLabel')}
            placeholder={placeHolder ? placeHolder : t('Phone.EnterPhoneNumber')}
            formatter={normalize}
            {...restOfProps}
            ref={ref}
        />
    );
});

Phone.displayName = 'Phone';

export default Phone;
